import type { TargetDefinition } from "./targetVisibility.js";

export const ticketTopics = [
  "identity",
  "networking",
  "filesystem",
  "security",
  "server",
  "m365_entra",
  "troubleshooting",
  "operations"
] as const;

export const ticketComplexities = ["intro", "basic", "intermediate", "advanced", "exam"] as const;
export const ticketCategories = ["servicedesk", "game", "practice", "assessment", "demo"] as const;
export const simulatorEnvironments = ["client", "server", "domain", "entra", "hybrid"] as const;

export type TicketTopic = (typeof ticketTopics)[number];
export type TicketComplexity = (typeof ticketComplexities)[number];
export type TicketCategory = (typeof ticketCategories)[number];
export type SimulatorEnvironment = (typeof simulatorEnvironments)[number];
export type TicketTemplateStatus = "draft" | "in_review" | "published" | "archived";

export interface TicketTemplate {
  id: string;
  institutionId: string | null;
  ownerUserId: string;
  title: string;
  description: string;
  topic: TicketTopic;
  subtopic?: string;
  complexity: TicketComplexity;
  category: TicketCategory;
  environment: SimulatorEnvironment;
  skills: string[];
  learningObjectives: string[];
  status: TicketTemplateStatus;
}

export interface TicketVersion {
  id: string;
  templateId: string;
  versionNumber: number;
  titleSnapshot: string;
  descriptionSnapshot: string;
  topicSnapshot: TicketTopic;
  complexitySnapshot: TicketComplexity;
  categorySnapshot: TicketCategory;
  environmentSnapshot: SimulatorEnvironment;
  skillsSnapshot: string[];
  learningObjectivesSnapshot: string[];
  targetsSnapshot: TargetDefinition[];
  startCriteriaSnapshot: unknown;
  assessmentSnapshot: unknown;
  publishedByUserId: string;
  publishedAt: string;
}

export interface TicketVersionSnapshotInput {
  template: TicketTemplate;
  versionNumber: number;
  targets: TargetDefinition[];
  startCriteria: unknown;
  assessment: unknown;
  publishedByUserId: string;
  publishedAt: string;
}

export interface PublishTicketVersionInput extends TicketVersionSnapshotInput {
  id: string;
}

export function createVersionSnapshot(input: TicketVersionSnapshotInput): Omit<TicketVersion, "id"> {
  return {
    templateId: input.template.id,
    versionNumber: input.versionNumber,
    titleSnapshot: input.template.title,
    descriptionSnapshot: input.template.description,
    topicSnapshot: input.template.topic,
    complexitySnapshot: input.template.complexity,
    categorySnapshot: input.template.category,
    environmentSnapshot: input.template.environment,
    skillsSnapshot: [...input.template.skills],
    learningObjectivesSnapshot: [...input.template.learningObjectives],
    targetsSnapshot: input.targets.map((target) => ({ ...target })),
    startCriteriaSnapshot: input.startCriteria,
    assessmentSnapshot: input.assessment,
    publishedByUserId: input.publishedByUserId,
    publishedAt: input.publishedAt
  };
}

export function publishTicketVersion(input: PublishTicketVersionInput): TicketVersion {
  return {
    id: input.id,
    ...createVersionSnapshot(input)
  };
}
